import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './EDNLPage.css';
import { demoApi, getWsNarrativeUrl, patternsApi } from './api';
import { getEdnlWorkspaceId, getEdnlWorkspaceName } from './workspaceUtils';
import { apiService } from '../../services/api';

export default function EDNLDemoPage() {
  const workspaceId = getEdnlWorkspaceId();
  const [workspaceName, setWorkspaceName] = useState(() => getEdnlWorkspaceName());
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState('bluewave_velocity');
  const [custId, setCustId] = useState('C-20198');
  const [scale, setScale] = useState(1);
  const [reset, setReset] = useState(false);
  const [amlMode, setAmlMode] = useState('mock');
  const [processId, setProcessId] = useState('');
  const [replayMs, setReplayMs] = useState(300);
  const [seedFirst, setSeedFirst] = useState(true);
  const [aggregateSource, setAggregateSource] = useState('builtin');
  const [log, setLog] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [config, setConfig] = useState(null);
  const [activePatternCount, setActivePatternCount] = useState(null); // null=unknown, number=loaded
  const [patternsLoading, setPatternsLoading] = useState(false);
  const [installBusy, setInstallBusy] = useState(false);

  const appendLog = useCallback((line) => {
    setLog((prev) => `${prev}\n${line}`.trim());
  }, []);

  const loadActivePatternCount = useCallback(async () => {
    if (!workspaceId) {
      setActivePatternCount(null);
      return;
    }
    setPatternsLoading(true);
    try {
      const data = await patternsApi.list(workspaceId);
      const list = Array.isArray(data) ? data : [];
      const n = list.filter(
        (p) => p && p.entity_type === 'Customer' && p.is_active !== false,
      ).length;
      setActivePatternCount(n);
    } catch (e) {
      setActivePatternCount(null);
      appendLog(`Pattern count fetch failed: ${e.message || e}`);
    } finally {
      setPatternsLoading(false);
    }
  }, [workspaceId, appendLog]);

  useEffect(() => {
    demoApi.presets().then((r) => {
      setPresets(r.presets || []);
      const bw = (r.presets || []).find((p) => p.id === 'bluewave_velocity');
      if (bw) setCustId(bw.default_cust_id);
    }).catch(() => {});
    demoApi.config().then(setConfig).catch(() => {});
  }, []);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiService.getWorkspacesForDropdown();
        if (cancelled) return;
        const list = (resp && resp.workspaces) || [];
        const match = list.find((w) => w.workspaceId === workspaceId);
        if (match?.workspaceName) setWorkspaceName(match.workspaceName);
      } catch {
        /* ignore: keep localStorage-derived name fallback */
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  useEffect(() => {
    loadActivePatternCount();
  }, [loadActivePatternCount]);

  const runSeed = async () => {
    if (!workspaceId) {
      appendLog('Select a workspace in the app (localStorage) first.');
      return;
    }
    setBusy(true);
    try {
      const res = await demoApi.seed({
        preset_id: presetId,
        workspace_id: workspaceId,
        cust_id: custId || undefined,
        scale,
        reset,
      });
      appendLog(`Seed OK: ${JSON.stringify(res)}`);
      if (res.cust_id) setCustId(res.cust_id);
    } catch (e) {
      appendLog(`Seed failed: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const installPatternsNow = async (replace = false) => {
    if (!workspaceId) {
      appendLog('No workspace selected; cannot install patterns.');
      return;
    }
    setInstallBusy(true);
    try {
      const res = await demoApi.installPatterns({ workspace_id: workspaceId, replace });
      appendLog(`Banking AML pack installed: ${JSON.stringify(res)}`);
      await loadActivePatternCount();
    } catch (e) {
      appendLog(`Install patterns failed: ${e.message || e}`);
    } finally {
      setInstallBusy(false);
    }
  };

  const runDemo = async (overrides = {}) => {
    const effectiveSource = overrides.aggregate_source || aggregateSource;
    if (amlMode === 'live') {
      const ok = window.confirm(
        'Live AML starts a real process execution (API cost and time). Continue?',
      );
      if (!ok) return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const res = await demoApi.run({
        workspace_id: workspaceId,
        cust_id: custId,
        aml_mode: amlMode,
        process_id: processId || undefined,
        replay_speed_ms: replayMs,
        seed_first: seedFirst,
        preset_id: seedFirst ? presetId : undefined,
        scale,
        reset: seedFirst && reset,
        aggregate_source: effectiveSource,
        capture_events: true,
      });
      setLastResult(res);
      const triggers = (res && res.process_triggers) || [];
      const triggerSummary = triggers.length
        ? ` -> triggered ${triggers.length} execution(s)`
        : '';
      appendLog(`Run finished${triggerSummary}: ${JSON.stringify({
        workspace_id: res?.workspace_id,
        cust_id: res?.cust_id,
        aml_mode: res?.aml_mode,
        aggregate_hits_emitted: res?.aggregate_hits_emitted,
        closed_frames: res?.closed_frame_ids?.length || 0,
        process_triggers: triggers.length,
      })}`);
    } catch (e) {
      appendLog(`Run failed: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const liveTriggers = ((lastResult && lastResult.process_triggers) || []).filter(
    (t) => t && t.execution_id && !String(t.execution_id).startsWith('mock-'),
  );

  const showZeroPatternsWarning = !!(
    lastResult &&
    lastResult.aggregate_source === 'workspace' &&
    (lastResult.workspace_patterns_loaded ?? 0) === 0
  );

  const showNoFrameClosedHint = !!(
    lastResult &&
    !showZeroPatternsWarning &&
    (lastResult.closed_frame_ids || []).length === 0 &&
    (lastResult.aggregate_hits_emitted ?? 0) === 0
  );

  return (
    <div className="user-home-layout__page">
      <div className="user-home-layout__page-header">
        <h1 className="user-home-layout__page-title">EDNL-Demo</h1>
        <p className="user-home-layout__page-subtitle">
          Run packaged AML Banking scenarios against the external{' '}
          <code>Banking</code> database — narratives, aggregates, frames, and Mock or Live AML.
        </p>
        <p style={{ fontSize: 13, color: 'var(--uh-text-secondary)', marginTop: 4 }}>
          Active workspace:{' '}
          {workspaceId ? (
            <>
              <strong>{workspaceName || '(name unknown)'}</strong>{' '}
              <code className="mono">{workspaceId}</code>
            </>
          ) : (
            <code className="mono">{'(none — pick one in EDNL → top of page)'}</code>
          )}
          . Audit entries and patterns are scoped to this id; switch workspaces from the EDNL page
          before clicking Run.
        </p>
      </div>

      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">Test case</h2>
        </div>
        <div style={{ display: 'grid', gap: 12, maxWidth: 560 }}>
          <label>
            Preset
            <select
              className="ednl-input"
              value={presetId}
              onChange={(e) => {
                setPresetId(e.target.value);
                const p = presets.find((x) => x.id === e.target.value);
                if (p?.default_cust_id) setCustId(p.default_cust_id);
              }}
              style={{ width: '100%', marginTop: 4 }}
            >
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </label>
          <label>
            Customer id
            <input
              className="ednl-input"
              value={custId}
              onChange={(e) => setCustId(e.target.value)}
              style={{ width: '100%', marginTop: 4 }}
            />
          </label>
          <label>
            Scale (repeat generator)
            <input
              type="number"
              min={1}
              max={20}
              className="ednl-input"
              value={scale}
              onChange={(e) => setScale(Number(e.target.value) || 1)}
              style={{ width: '100%', marginTop: 4 }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={reset} onChange={(e) => setReset(e.target.checked)} />
            Truncate Banking tables on seed (reset)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={seedFirst} onChange={(e) => setSeedFirst(e.target.checked)} />
            Seed before run
          </label>
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="ednl-btn ednl-btn--primary" disabled={busy} onClick={runSeed}>
            Seed only
          </button>
        </div>
      </div>

      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">Aggregate patterns</h2>
        </div>
        <p style={{ fontSize: 13, color: 'var(--uh-text-secondary)', marginBottom: 8 }}>
          <strong>Built-in</strong> uses the demo&apos;s hardcoded wire-velocity rule. <strong>From workspace</strong> uses
          active patterns from <strong>EDNL → Patterns</strong> (install the Banking AML pack there first, same workspace id).
        </p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button
            type="button"
            className={`ednl-tabs__btn ${aggregateSource === 'builtin' ? 'ednl-tabs__btn--active' : ''}`}
            onClick={() => setAggregateSource('builtin')}
          >
            Built-in (demo default)
          </button>
          <button
            type="button"
            className={`ednl-tabs__btn ${aggregateSource === 'workspace' ? 'ednl-tabs__btn--active' : ''}`}
            onClick={() => setAggregateSource('workspace')}
          >
            From workspace (Patterns tab)
          </button>
        </div>
        {aggregateSource === 'workspace' && (
          <div
            style={{
              fontSize: 13,
              padding: 10,
              borderRadius: 6,
              border: '1px solid',
              ...(activePatternCount === 0
                ? { background: '#fffbeb', borderColor: '#fde68a', color: '#92400e' }
                : { background: '#f9fafb', borderColor: '#e5e7eb', color: 'var(--uh-text-secondary)' }),
            }}
          >
            <div style={{ marginBottom: 8 }}>
              {patternsLoading ? (
                'Loading active patterns...'
              ) : activePatternCount === null ? (
                <>Could not read active patterns for this workspace.</>
              ) : (
                <>
                  Active <strong>Customer</strong> patterns in workspace{' '}
                  <strong>{workspaceName || '(name unknown)'}</strong>{' '}
                  <code className="mono">{workspaceId || '(none)'}</code>:{' '}
                  <strong>{activePatternCount}</strong>
                  {activePatternCount === 0 && (
                    <>
                      {' '}— with zero patterns the run cannot produce aggregate hits, the
                      frame stays open, and no AML process is triggered.
                    </>
                  )}
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="ednl-btn ednl-btn--sm"
                disabled={installBusy || !workspaceId}
                onClick={() => installPatternsNow(false)}
              >
                {installBusy ? 'Installing...' : 'Install Banking AML reference patterns'}
              </button>
              <button
                type="button"
                className="ednl-btn ednl-btn--sm"
                disabled={patternsLoading}
                onClick={loadActivePatternCount}
              >
                Refresh count
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">AML execution mode</h2>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button
            type="button"
            className={`ednl-tabs__btn ${amlMode === 'mock' ? 'ednl-tabs__btn--active' : ''}`}
            onClick={() => setAmlMode('mock')}
          >
            Mock AML
          </button>
          <button
            type="button"
            className={`ednl-tabs__btn ${amlMode === 'live' ? 'ednl-tabs__btn--active' : ''}`}
            onClick={() => setAmlMode('live')}
          >
            Live AML
          </button>
        </div>
        {amlMode === 'live' && (
          <label>
            Process id (optional if EDNL_DEMO_AML_PROCESS_ID is set on server)
            <input
              className="ednl-input"
              value={processId}
              onChange={(e) => setProcessId(e.target.value)}
              placeholder="UUID of ProcessDefinition"
              style={{ width: '100%', marginTop: 4 }}
            />
          </label>
        )}
        <label style={{ marginTop: 12, display: 'block' }}>
          Replay delay (ms between rows)
          <input
            type="number"
            min={0}
            max={60000}
            className="ednl-input"
            value={replayMs}
            onChange={(e) => setReplayMs(Number(e.target.value) || 0)}
            style={{ width: '100%', marginTop: 4 }}
          />
        </label>
        <div style={{ marginTop: 16 }}>
          <button type="button" className="ednl-btn ednl-btn--primary" disabled={busy} onClick={() => runDemo()}>
            {busy ? 'Running…' : 'Run demo'}
          </button>
        </div>
        {config && (
          <p style={{ marginTop: 12, fontSize: 13, color: 'var(--uh-text-secondary)' }}>
            Server: BANKING_DATABASE_URL set={String(config.banking_database_url_set)}; AML process id
            configured={String(config.aml_process_id_configured)}
          </p>
        )}
      </div>

      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">Deep links</h2>
        </div>
        <p style={{ marginBottom: 8 }}>Open the main EDNL console to observe WebSocket events and frames.</p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Link className="ednl-btn ednl-btn--secondary" to="/user-home/ednl">
            EDNL (tabs)
          </Link>
          <a className="ednl-btn ednl-btn--secondary" href={`${window.location.origin}/user-home/ednl`} onClick={(e) => { e.preventDefault(); window.open(`${window.location.origin}/user-home/ednl`, '_blank'); }}>
            EDNL new tab
          </a>
          <span className="ednl-btn ednl-btn--secondary" style={{ cursor: 'default', opacity: 0.85 }}>
            WS: {getWsNarrativeUrl()}
          </span>
        </div>
      </div>

      {lastResult && (
        <div className="ednl-card">
          <div className="ednl-card__header">
            <h3 className="ednl-card__title">Last result</h3>
            <Link className="ednl-btn ednl-btn--sm" to="/user-home/ednl?tab=audit">
              Open Audit tab
            </Link>
          </div>
          <div style={{ display: 'grid', gap: 4, fontSize: 13, marginBottom: 8 }}>
            <div>
              Workspace: <strong>{workspaceName || '(name unknown)'}</strong>{' '}
              <code className="mono">{lastResult.workspace_id || workspaceId}</code>
            </div>
            <div>
              AML mode: <strong>{lastResult.aml_mode}</strong>
              {lastResult.process_id && (
                <>
                  {' '}· Process id: <code className="mono">{lastResult.process_id}</code>
                </>
              )}
            </div>
            <div>
              Transactions: {lastResult.transactions_processed} · Closed frames:{' '}
              {(lastResult.closed_frame_ids || []).length} · Aggregate hits:{' '}
              {lastResult.aggregate_hits_emitted ?? 0}
            </div>
            {lastResult.event_counts && (
              <div style={{ color: 'var(--uh-text-secondary)' }}>
                Events captured: {Object.entries(lastResult.event_counts).map(([k, v]) => `${k}=${v}`).join(' · ')}
              </div>
            )}
          </div>

          {showZeroPatternsWarning && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: 12, marginBottom: 8, color: '#92400e' }}>
              <strong style={{ fontSize: 13 }}>No aggregate patterns in this workspace</strong>
              <div style={{ fontSize: 12, marginTop: 6 }}>
                You ran with <code>aggregate_source = workspace</code> but workspace{' '}
                <strong>{workspaceName || '(name unknown)'}</strong>{' '}
                <code className="mono">{lastResult.workspace_id || workspaceId}</code> has zero
                active <code>Customer</code> patterns. No aggregate hit was produced, so the frame
                never received the <code>alert_triggered</code> semantic close trigger and no AML
                process was started.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                <button
                  type="button"
                  className="ednl-btn ednl-btn--sm ednl-btn--primary"
                  disabled={installBusy || busy}
                  onClick={() => installPatternsNow(false)}
                >
                  {installBusy ? 'Installing...' : 'Install Banking AML pack into this workspace'}
                </button>
                <button
                  type="button"
                  className="ednl-btn ednl-btn--sm"
                  disabled={busy}
                  onClick={() => {
                    setAggregateSource('builtin');
                    runDemo({ aggregate_source: 'builtin' });
                  }}
                >
                  Switch to Built-in and rerun
                </button>
              </div>
            </div>
          )}

          {showNoFrameClosedHint && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, marginBottom: 8, color: 'var(--uh-text-secondary)' }}>
              <strong style={{ fontSize: 13, color: '#111827' }}>Frame still open — nothing routed</strong>
              <div style={{ fontSize: 12, marginTop: 6 }}>
                The replay produced narratives but no aggregate hit reached its threshold, so the
                frame did not close on the <code>alert_triggered</code> semantic completeness
                rule. Try a higher <code>scale</code>, a velocity-heavy preset like{' '}
                <code>bluewave_velocity</code>, or close the frame manually from{' '}
                <Link to="/user-home/ednl?tab=frames">EDNL → Frames</Link>.
              </div>
            </div>
          )}

          {liveTriggers.length > 0 && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: 12, marginBottom: 8 }}>
              <strong style={{ fontSize: 13 }}>Live AML executions triggered ({liveTriggers.length})</strong>
              <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 13 }}>
                {liveTriggers.map((t, i) => (
                  <li key={`${t.execution_id}-${i}`} style={{ marginBottom: 4 }}>
                    <code className="mono">{t.execution_id}</code>{' '}
                    <Link
                      className="ednl-btn ednl-btn--sm"
                      to={`/user-home/process-maps/${t.process_id}/run`}
                      style={{ marginLeft: 6 }}
                    >
                      Open execution
                    </Link>
                  </li>
                ))}
              </ul>
              <div style={{ fontSize: 11, color: 'var(--uh-text-secondary)', marginTop: 6 }}>
                Each link opens the Process Map run page where you can see node results, traces,
                and final outputs for this execution.
              </div>
            </div>
          )}

          {(lastResult.process_triggers || []).filter((t) => String(t?.execution_id || '').startsWith('mock-')).length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--uh-text-secondary)', marginBottom: 8 }}>
              Mock AML returned synthetic execution ids; no real ProcessExecution row was created.
              Switch to <strong>Live AML</strong> with a real <code>process_id</code> to get a clickable
              execution link.
            </div>
          )}

          <details>
            <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--uh-text-secondary)' }}>
              Full JSON
            </summary>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
              {JSON.stringify(lastResult, null, 2)}
            </pre>
          </details>
        </div>
      )}

      <div className="ednl-card">
        <h3 className="ednl-card__title">Log</h3>
        <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, minHeight: 80 }}>{log || '—'}</pre>
      </div>
    </div>
  );
}
